import { Component, OnInit, OnDestroy, inject, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AppConstants } from 'src/app/constants/app-constants';
import { NLSTransaction } from 'src/app/model/NLSTransaction';
import { NLSTransactionService } from 'src/app/services/nls-transaction/nls-transaction.service';
import { DialogService } from 'src/app/services/dialog/dialog.service';

@Component({
    selector: 'app-borrower-upcoming-transactions-table',
    templateUrl: './borrower-upcoming-transactions-table.component.html',
    styleUrl: './borrower-upcoming-transactions-table.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BorrowerUpcomingTransactionsTableComponent implements OnInit, OnDestroy {
    /** Dependency injections */
    private _nlsTransactionService = inject(NLSTransactionService);
    private _dialogService: DialogService = inject(DialogService);
    private _router = inject(Router);

    /** Upcoming transactions to render (current_balance > 0) */
    upcomingTransactions: NLSTransaction[] = [];

    /** Loading state */
    loading = true;

    /** Table columns - commented out fields specified from @product */
    displayedColumns: string[] = [
        'transaction_id',
        'supplier_company',
        'amount',
        'current_balance',
        'next_payment_due',
        'next_payment_amount',
        'actions',
    ];

    /** Subscription for data */
    private transactionEventsSub?: Subscription;

    /** Router links */
    public transactionDetailsLink = AppConstants.MENU_TRANSACTION_DETAILS;

    /**
     * OnInit, subscribe to data changes and initialize data source
     */
    ngOnInit(): void {
        // Subscribe to transactions
        this.transactionEventsSub = this._nlsTransactionService.changes$.subscribe((transactionEvents: NLSTransaction[]) => {
            // Filter for upcoming transactions (current_balance > 0)
            this.upcomingTransactions = transactionEvents.filter(transaction => transaction.current_balance > 0);
            this.loading = false;
        });

        // Get latest data from nls transaction service
        const transactionEvents = this._nlsTransactionService.getEntities();
        if (transactionEvents) {
            this.upcomingTransactions = transactionEvents.filter((transaction: NLSTransaction) => transaction.current_balance > 0);
            this.loading = false;
        }
    }

    /**
     * On component destroy, unsubscribe from all subscriptions
     */
    ngOnDestroy(): void {
        if (this.transactionEventsSub) {
            this.transactionEventsSub.unsubscribe();
        }
    }

    /**
     * Format currency values
     * 
     * @param amount The amount to format
     * @return The formatted currency string
     */
    formatCurrency(amount: number): string {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    }

    /**
     * Format date values
     * 
     * @param date The date to format
     * @return The formatted date string
     */
    formatDate(date: Date | null): string {
        if (!date) return 'N/A';
        return new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        }).format(new Date(date));
    }

    /**
     * Handle Pay Now button click
     * 
     * @param transaction The upcoming transaction to process payment for
     */
    onPayNow(transaction: NLSTransaction): void {
        // Open the make a payment dialog
        this._dialogService.openMakePaymentDialog({ transaction });
    }
}
